import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ImportLeaves from './ImportLeaves';

const LeaveLedger = () => {
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    
    // Filter and search states
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');
    const [filterEmployee, setFilterEmployee] = useState('all');
    const [filterLeaveType, setFilterLeaveType] = useState('all');
    const [filterDateRange, setFilterDateRange] = useState({
        from: '',
        to: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    // Dropdown data
    const [employees, setEmployees] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);

    const [formData, setFormData] = useState({
        emp_id: '',
        leave_type_id: '',
        tdate: '',
        earned: '',
        consumed: ''
    });
    const [employeeSearchText, setEmployeeSearchText] = useState('');

    const [validationErrors, setValidationErrors] = useState({});
    
    const [userPermissions, setUserPermissions] = useState({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        loaded: false
    });

    const [summary, setSummary] = useState({
        totalEarned: 0,
        totalConsumed: 0,
        netBalance: 0,
        entriesCount: 0,
        uniqueEmployees: 0
    });

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchUserPermissions = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
                return;
            }

            const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.permissions) {
                const ledgerPermissions = response.data.permissions.find(
                    p => p.module_name === 'LeaveLedger' || p.module_name === 'LeaveManagement'
                );

                if (ledgerPermissions) {
                    setUserPermissions({
                        can_view: Boolean(ledgerPermissions.can_view),
                        can_add: Boolean(ledgerPermissions.can_add),
                        can_edit: Boolean(ledgerPermissions.can_edit),
                        can_delete: Boolean(ledgerPermissions.can_delete),
                        can_export: Boolean(ledgerPermissions.can_export),
                        loaded: true
                    });
                } else {
                    setUserPermissions({
                        can_view: false,
                        can_add: false,
                        can_edit: false,
                        can_delete: false,
                        can_export: false,
                        loaded: true
                    });
                }
            } else {
                setUserPermissions({
                    can_view: false,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    can_export: false,
                    loaded: true
                });
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setUserPermissions({
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                can_export: false,
                loaded: true
            });
        }
    }, [API_BASE]);

    const fetchEmployees = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_employees.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setEmployees(response.data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }, [API_BASE]);

    const fetchLeaveTypes = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/leave/get_leave_types.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLeaveTypes(response.data.leaveTypes || response.data.leave_types || []);
            }
        } catch (error) {
            console.error('Error fetching leave types:', error);
        }
    }, [API_BASE]);

    const fetchCompanies = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_companies.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setCompanies(response.data.companies || []);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    }, [API_BASE]);

    const fetchLedgerEntries = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE}/leave/get_leave_ledger.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 15000
            });

            if (response.data && response.data.success) {
                const entries = response.data.entries || [];
                setLedgerEntries(entries);
                
                // Calculate summary
                const summary = entries.reduce((acc, entry) => ({
                    totalEarned: acc.totalEarned + parseFloat(entry.earned || 0),
                    totalConsumed: acc.totalConsumed + parseFloat(entry.consumed || 0),
                    entriesCount: entries.length,
                    uniqueEmployees: new Set(entries.map(e => e.emp_id)).size
                }), {
                    totalEarned: 0,
                    totalConsumed: 0,
                    entriesCount: 0,
                    uniqueEmployees: 0
                });

                summary.netBalance = summary.totalEarned - summary.totalConsumed;
                setSummary(summary);
                setError('');
            } else {
                setLedgerEntries([]);
                const errorMsg = response.data?.error || 'Failed to fetch leave ledger entries';
                setError(errorMsg);
            }
        } catch (error) {
            setLedgerEntries([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch leave ledger entries';
            setError(errorMsg);
            console.error('Error fetching leave ledger entries:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE]);

    const fetchCompanyId = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}/get_user_company.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setSelectedCompany(response.data.company_id);
            }
        } catch (error) {
            console.error('Error fetching company ID:', error);
        }
    }, [API_BASE]);

    useEffect(() => {
        const initializeData = async () => {
            await fetchUserPermissions();
            await fetchCompanyId();
            await fetchEmployees();
            await fetchLeaveTypes();
            await fetchCompanies();
            await fetchLedgerEntries();
        };

        initializeData();
    }, [fetchUserPermissions, fetchCompanyId, fetchEmployees, fetchLeaveTypes, fetchCompanies, fetchLedgerEntries]);

    const canViewLedger = useCallback(() => userPermissions.can_view, [userPermissions.can_view]);
    const canAddEntry = useCallback(() => userPermissions.can_add, [userPermissions.can_add]);
    const canEditEntry = useCallback(() => userPermissions.can_edit, [userPermissions.can_edit]);
    const canDeleteEntry = useCallback(() => userPermissions.can_delete, [userPermissions.can_delete]);
    const canExportLedger = useCallback(() => userPermissions.can_export, [userPermissions.can_export]);
    const canImportLeaves = useCallback(() => userPermissions.can_add, [userPermissions.can_add]);

    const handleImportLeaves = async (records, setProgress) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            if (!Array.isArray(records) || records.length === 0) {
                throw new Error('No records found in import file');
            }

            const totalRecords = records.length;
            let processed = 0;
            const batchSize = 50;

            for (let i = 0; i < records.length; i += batchSize) {
                const batch = records.slice(i, i + batchSize);

                const response = await axios.post(
                    `${API_BASE}imports/import_leave_ledger.php`,
                    { records: batch },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (!response.data.success) {
                    throw new Error(response.data.error || 'Import failed');
                }

                processed += batch.length;
                setProgress(Math.round((processed / totalRecords) * 100));
            }

            await fetchLedgerEntries();
            alert('Leave import completed successfully');
        } catch (error) {
            throw new Error(error.response?.data?.error || error.message || 'Import failed');
        }
    };

    const handleAdd = () => {
        if (!canAddEntry()) {
            alert('You do not have permission to add leave ledger entries');
            return;
        }
        
        // Set today's date as default
        const today = new Date().toISOString().split('T')[0];
        
        setFormData({
            emp_id: '',
            leave_type_id: '',
            tdate: today,
            earned: '',
            consumed: ''
        });
        setEmployeeSearchText('');
        setValidationErrors({});
        setShowAddModal(true);
    };

    const handleEdit = (entry) => {
        if (!canEditEntry()) {
            alert('You do not have permission to edit leave ledger entries');
            return;
        }
        setSelectedEntry(entry);
        setFormData({
            emp_id: entry.emp_id,
            leave_type_id: entry.leave_type_id,
            tdate: entry.tdate,
            earned: entry.earned,
            consumed: entry.consumed
        });
        const selectedEmployee = employees.find(emp => parseInt(emp.emp_id, 10) === parseInt(entry.emp_id, 10));
        setEmployeeSearchText(
            selectedEmployee
                ? `${selectedEmployee.first_name} ${selectedEmployee.last_name || ''} (${selectedEmployee.emp_code})`
                : getEmployeeName(entry.emp_id)
        );
        setValidationErrors({});
        setShowEditModal(true);
    };

    const handleView = (entry) => {
        setSelectedEntry(entry);
        setShowViewModal(true);
    };

    const handleDelete = (entry) => {
        if (!canDeleteEntry()) {
            alert('You do not have permission to delete leave ledger entries');
            return;
        }
        setSelectedEntry(entry);
        setShowDeleteModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }

        // If changing employee or leave type, clear any existing balance warnings
        if (name === 'emp_id' || name === 'leave_type_id') {
            setValidationErrors(prev => ({ ...prev, balance_warning: '' }));
        }
    };

    const getCompanyEmployees = useCallback(() => {
        if (selectedCompany === null || selectedCompany === undefined || selectedCompany === '') {
            return employees;
        }
        return employees.filter(emp => parseInt(emp.company_id, 10) === parseInt(selectedCompany, 10));
    }, [employees, selectedCompany]);

    const getEmployeeOptionLabel = useCallback((emp) => {
        return `${emp.first_name} ${emp.last_name || ''} (${emp.emp_code})`;
    }, []);

    const handleEmployeeSearchChange = (e) => {
        const value = e.target.value;
        const normalized = value.trim().toLowerCase();
        setEmployeeSearchText(value);

        const matchedEmployee = getCompanyEmployees().find(
            emp => getEmployeeOptionLabel(emp).toLowerCase() === normalized
        );

        setFormData(prev => ({
            ...prev,
            emp_id: matchedEmployee ? String(matchedEmployee.emp_id) : ''
        }));

        if (validationErrors.emp_id) {
            setValidationErrors(prev => ({ ...prev, emp_id: '' }));
        }
        setValidationErrors(prev => ({ ...prev, balance_warning: '' }));
    };

    const validateForm = async () => {
        const errors = {};

        if (!formData.emp_id) {
            errors.emp_id = 'Employee is required';
        }

        if (!formData.leave_type_id) {
            errors.leave_type_id = 'Leave type is required';
        }

        if (!formData.tdate) {
            errors.tdate = 'Date is required';
        } else {
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(formData.tdate)) {
                errors.tdate = 'Invalid date format (YYYY-MM-DD)';
            }
        }

        // Validate earned days
        if (formData.earned && formData.earned !== '') {
            if (!isNumeric(formData.earned)) {
                errors.earned = 'Must be a valid number';
            } else {
                const earned = parseFloat(formData.earned);
                if (earned < 0) {
                    errors.earned = 'Cannot be negative';
                }
                if (earned > 365) {
                    errors.earned = 'Exceeds maximum allowed (365)';
                }
            }
        }

        // Validate consumed days
        if (formData.consumed && formData.consumed !== '') {
            if (!isNumeric(formData.consumed)) {
                errors.consumed = 'Must be a valid number';
            } else {
                const consumed = parseFloat(formData.consumed);
                if (consumed < 0) {
                    errors.consumed = 'Cannot be negative';
                }
                if (consumed > 365) {
                    errors.consumed = 'Exceeds maximum allowed (365)';
                }
            }
        }

        // At least one of earned or consumed must be provided
        if ((!formData.earned || formData.earned === '') && 
            (!formData.consumed || formData.consumed === '')) {
            errors.earned = 'Either Earned or Consumed days must be provided';
            errors.consumed = 'Either Earned or Consumed days must be provided';
        }

        // Check if both earned and consumed are provided
        if (formData.earned && formData.earned !== '' && 
            formData.consumed && formData.consumed !== '') {
            const earned = parseFloat(formData.earned);
            const consumed = parseFloat(formData.consumed);
            
            if (earned > 0 && consumed > 0) {
                errors.balance_warning = 'Warning: Both earned and consumed days are being added. This may affect leave balance.';
            }
        }

        // Check leave balance if it's a consumption entry
        if (formData.emp_id && formData.leave_type_id && 
            formData.consumed && formData.consumed !== '' && 
            !formData.earned) {
            
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.post(`${API_BASE}/leave/check_leave_balance.php`, {
                    emp_id: formData.emp_id,
                    leave_type_id: formData.leave_type_id,
                    requested_days: formData.consumed
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success && !response.data.has_balance) {
                    errors.consumed = `Insufficient leave balance. Available: ${response.data.available_balance} days`;
                }
            } catch (error) {
                console.error('Error checking leave balance:', error);
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const isNumeric = (value) => {
        return !isNaN(parseFloat(value)) && isFinite(value);
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();

        const isValid = await validateForm();
        if (!isValid) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            
            // Format the data
            const submitData = {
                ...formData,
                earned: formData.earned ? parseFloat(formData.earned).toFixed(2) : '0.00',
                consumed: formData.consumed ? parseFloat(formData.consumed).toFixed(2) : '0.00'
            };

            const response = await axios.post(`${API_BASE}/leave/add_leave_ledger.php`,
                submitData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchLedgerEntries();
                setShowAddModal(false);
                alert('Leave ledger entry added successfully');
            } else {
                setError(response.data.error || 'Failed to add leave ledger entry');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to add leave ledger entry');
            console.error('Error adding leave ledger entry:', error);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        const isValid = await validateForm();
        if (!isValid) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            
            // Format the data
            const submitData = {
                id: selectedEntry.id,
                ...formData,
                earned: formData.earned ? parseFloat(formData.earned).toFixed(2) : '0.00',
                consumed: formData.consumed ? parseFloat(formData.consumed).toFixed(2) : '0.00'
            };

            const response = await axios.post(`${API_BASE}/leave/update_leave_ledger.php`,
                submitData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchLedgerEntries();
                setShowEditModal(false);
                alert('Leave ledger entry updated successfully');
            } else {
                setError(response.data.error || 'Failed to update leave ledger entry');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to update leave ledger entry');
            console.error('Error updating leave ledger entry:', error);
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}/leave/delete_leave_ledger.php`,
                { id: selectedEntry.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                await fetchLedgerEntries();
                setShowDeleteModal(false);
                alert('Leave ledger entry deleted successfully');
            } else {
                setError(response.data.error || 'Failed to delete leave ledger entry');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to delete leave ledger entry');
            console.error('Error deleting leave ledger entry:', error);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSortBy('date_desc');
        setFilterEmployee('all');
        setFilterLeaveType('all');
        setFilterDateRange({ from: '', to: '' });
    };

    const getEmployeeName = (empId) => {
        const employee = employees.find(e => e.emp_id === parseInt(empId));
        if (employee) {
            return `${employee.first_name} ${employee.last_name || ''} (${employee.emp_code})`;
        }
        return `Employee #${empId}`;
    };

    const getLeaveTypeName = (leaveTypeId) => {
        const leaveType = leaveTypes.find(l => l.leave_type_id === parseInt(leaveTypeId));
        return leaveType ? leaveType.leave_name : `Leave Type #${leaveTypeId}`;
    };

    const getCompanyName = (companyId) => {
        const company = companies.find(c => c.company_id === parseInt(companyId));
        return company ? company.company_name : `Company #${companyId}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Filter and sort entries
    const filteredEntries = ledgerEntries.filter(entry => {
        // Search filter
        const matchesSearch = searchTerm === '' ||
            getEmployeeName(entry.emp_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
            getLeaveTypeName(entry.leave_type_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.tdate?.includes(searchTerm) ||
            entry.earned?.toString().includes(searchTerm) ||
            entry.consumed?.toString().includes(searchTerm);

        // Employee filter
        const matchesEmployee = filterEmployee === 'all' || 
            entry.emp_id.toString() === filterEmployee;

        // Leave type filter
        const matchesLeaveType = filterLeaveType === 'all' || 
            entry.leave_type_id.toString() === filterLeaveType;

        // Date range filter
        let matchesDateRange = true;
        if (filterDateRange.from && filterDateRange.to) {
            const entryDate = new Date(entry.tdate);
            const fromDate = new Date(filterDateRange.from);
            const toDate = new Date(filterDateRange.to);
            matchesDateRange = entryDate >= fromDate && entryDate <= toDate;
        } else if (filterDateRange.from) {
            const entryDate = new Date(entry.tdate);
            const fromDate = new Date(filterDateRange.from);
            matchesDateRange = entryDate >= fromDate;
        } else if (filterDateRange.to) {
            const entryDate = new Date(entry.tdate);
            const toDate = new Date(filterDateRange.to);
            matchesDateRange = entryDate <= toDate;
        }

        return matchesSearch && matchesEmployee && matchesLeaveType && matchesDateRange;
    });

    const sortedEntries = [...filteredEntries].sort((a, b) => {
        switch (sortBy) {
            case 'date_asc':
                return new Date(a.tdate) - new Date(b.tdate);
            case 'date_desc':
                return new Date(b.tdate) - new Date(a.tdate);
            case 'earned_desc':
                return parseFloat(b.earned || 0) - parseFloat(a.earned || 0);
            case 'earned_asc':
                return parseFloat(a.earned || 0) - parseFloat(b.earned || 0);
            case 'consumed_desc':
                return parseFloat(b.consumed || 0) - parseFloat(a.consumed || 0);
            case 'consumed_asc':
                return parseFloat(a.consumed || 0) - parseFloat(b.consumed || 0);
            case 'employee':
                return getEmployeeName(a.emp_id).localeCompare(getEmployeeName(b.emp_id));
            case 'leave_type':
                return getLeaveTypeName(a.leave_type_id).localeCompare(getLeaveTypeName(b.leave_type_id));
            default:
                return new Date(b.tdate) - new Date(a.tdate);
        }
    });

    const exportToCSV = () => {
        if (!canExportLedger()) {
            alert('You do not have permission to export data');
            return;
        }

        try {
            const headers = ['ID', 'Company', 'Employee', 'Leave Type', 'Date', 'Earned', 'Consumed', 'Created On'];
            
            const rows = sortedEntries.map(entry => [
                entry.id,
                getCompanyName(entry.company_id),
                getEmployeeName(entry.emp_id),
                getLeaveTypeName(entry.leave_type_id),
                formatDate(entry.tdate),
                entry.earned || '0.00',
                entry.consumed || '0.00',
                formatDateTime(entry.create_on)
            ]);

            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            
            const fileName = `leave_ledger_export_${new Date().toISOString().split('T')[0]}.csv`;
            
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            alert('Export completed successfully');
        } catch (error) {
            console.error('Error exporting data:', error);
            alert('Failed to export data');
        }
    };

    if (loading || !userPermissions.loaded) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading leave ledger entries...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!canViewLedger()) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                                <h3 className="mt-3 text-warning">Access Denied</h3>
                                <p className="text-muted">You do not have permission to view leave ledger entries.</p>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    <i className="bi bi-speedometer2 me-2"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/dashboard')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-journal-bookmark-fill me-2 text-primary"></i>
                                                Leave Ledger
                                                {!canAddEntry() && !canEditEntry() && !canDeleteEntry() && (
                                                    <span className="badge bg-warning ms-2">View Only</span>
                                                )}
                                            </h2>
                                            <p className="text-muted mb-0">
                                                Track employee leave earned and consumed
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => navigate('/leave-management')}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back
                                        </button>
                                        {canAddEntry() && (
                                            <>
                                                <button
                                                    className="btn btn-outline-success me-2"
                                                    onClick={() => setShowImportModal(true)}
                                                >
                                                    <i className="bi bi-upload me-2"></i>
                                                    Import Leaves
                                                </button>
                                                <button
                                                    className="btn btn-primary text-white"
                                                    onClick={handleAdd}
                                                >
                                                    <i className="bi bi-plus-circle me-2"></i>
                                                    Add Entry
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/dashboard')}
                                    >
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        Dashboard
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/employees')}
                                    >
                                        <i className="bi bi-people me-2"></i>
                                        Employees
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/leave-master')}
                                    >
                                        <i className="bi bi-gear me-2"></i>
                                        Leave Master
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-primary text-white w-100 d-flex align-items-center justify-content-center"
                                        disabled
                                    >
                                        <i className="bi bi-journal-bookmark-fill me-2"></i>
                                        Leave Ledger
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/leave-applications')}
                                    >
                                        <i className="bi bi-file-text me-2"></i>
                                        Applications
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/Menu')}
                                    >
                                        <i className="bi bi-menu-button me-2"></i>
                                        Menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="bi bi-calculator fs-1 text-primary"></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1">Total Earned</h6>
                                    <h3 className="mb-0 fw-bold text-success">
                                        {summary.totalEarned.toFixed(2)}
                                        <small className="fs-6 text-muted ms-1">days</small>
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="bi bi-calendar-check fs-1 text-danger"></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1">Total Consumed</h6>
                                    <h3 className="mb-0 fw-bold text-danger">
                                        {summary.totalConsumed.toFixed(2)}
                                        <small className="fs-6 text-muted ms-1">days</small>
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="bi bi-pie-chart fs-1 text-info"></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1">Net Balance</h6>
                                    <h3 className="mb-0 fw-bold text-info">
                                        {summary.netBalance.toFixed(2)}
                                        <small className="fs-6 text-muted ms-1">days</small>
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="bi bi-people fs-1 text-warning"></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1">Employees</h6>
                                    <h3 className="mb-0 fw-bold text-warning">
                                        {summary.uniqueEmployees}
                                        <small className="fs-6 text-muted ms-1">active</small>
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchLedgerEntries}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Filter Bar */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-4">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search by employee, leave type, date..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterEmployee}
                                        onChange={(e) => setFilterEmployee(e.target.value)}
                                    >
                                        <option value="all">All Employees</option>
                                        {employees.map(emp => (
                                            <option key={emp.emp_id} value={emp.emp_id}>
                                                {emp.first_name} {emp.last_name} ({emp.emp_code})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={filterLeaveType}
                                        onChange={(e) => setFilterLeaveType(e.target.value)}
                                    >
                                        <option value="all">All Leave Types</option>
                                        {leaveTypes.map(lt => (
                                            <option key={lt.leave_type_id} value={lt.leave_type_id}>
                                                {lt.leave_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="date_desc">Newest First</option>
                                        <option value="date_asc">Oldest First</option>
                                        <option value="earned_desc">Earned (High-Low)</option>
                                        <option value="earned_asc">Earned (Low-High)</option>
                                        <option value="consumed_desc">Consumed (High-Low)</option>
                                        <option value="consumed_asc">Consumed (Low-High)</option>
                                        <option value="employee">Employee Name</option>
                                        <option value="leave_type">Leave Type</option>
                                    </select>
                                </div>
                                <div className="col-md-2 text-end">
                                    <div className="btn-group w-100">
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setShowFilters(!showFilters)}
                                            title="Advanced Filters"
                                        >
                                            <i className={`bi bi-funnel${showFilters ? '-fill' : ''}`}></i>
                                            <span className="ms-1 d-none d-md-inline">Filters</span>
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={fetchLedgerEntries}
                                            title="Refresh"
                                        >
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </button>
                                        {canExportLedger() && (
                                            <button
                                                className="btn btn-outline-success"
                                                onClick={exportToCSV}
                                                title="Export to CSV"
                                            >
                                                <i className="bi bi-download"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Advanced Filters */}
                            {showFilters && (
                                <div className="row mt-3 pt-3 border-top">
                                    <div className="col-md-4">
                                        <label className="form-label small text-muted">Date From</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={filterDateRange.from}
                                            onChange={(e) => setFilterDateRange(prev => ({ ...prev, from: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label small text-muted">Date To</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={filterDateRange.to}
                                            onChange={(e) => setFilterDateRange(prev => ({ ...prev, to: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-4 d-flex align-items-end">
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={clearFilters}
                                        >
                                            <i className="bi bi-eraser me-1"></i>
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Ledger Table */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="card-title mb-0">
                                    <i className="bi bi-table me-2"></i>
                                    Leave Ledger Entries
                                    <span className="badge bg-primary ms-2">{filteredEntries.length} records</span>
                                </h6>
                                <div>
                                    <span className="text-muted small me-2">
                                        <i className="bi bi-plus-circle-fill text-success me-1"></i> Earned
                                    </span>
                                    <span className="text-muted small">
                                        <i className="bi bi-dash-circle-fill text-danger me-1"></i> Consumed
                                    </span>
                                </div>
                            </div>

                            {sortedEntries.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-journal-x display-1 text-muted"></i>
                                    <h5 className="mt-3 text-muted">No entries found</h5>
                                    <p className="text-muted mb-0">
                                        {ledgerEntries.length === 0
                                            ? "No leave ledger entries available. Add your first entry."
                                            : "No entries match your search criteria."
                                        }
                                    </p>
                                    {ledgerEntries.length === 0 && canAddEntry() && (
                                        <button
                                            className="btn btn-primary text-white mt-3"
                                            onClick={handleAdd}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Add First Entry
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="small">ID</th>
                                                <th className="small">Employee</th>
                                                <th className="small">Leave Type</th>
                                                <th className="small">Date</th>
                                                <th className="small text-end">Earned</th>
                                                <th className="small text-end">Consumed</th>
                                                <th className="small text-center">Created On</th>
                                                <th className="small text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedEntries.map((entry) => (
                                                <tr key={entry.id}>
                                                    <td>
                                                        <span className="badge bg-light text-dark">
                                                            #{entry.id}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-2">
                                                                <i className="bi bi-person-fill text-primary"></i>
                                                            </div>
                                                            <div>
                                                                <div className="fw-medium">
                                                                    {getEmployeeName(entry.emp_id)}
                                                                </div>
                                                                <small className="text-muted">
                                                                    Company: {getCompanyName(entry.company_id)}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-info bg-opacity-10 text-info border border-info">
                                                            {getLeaveTypeName(entry.leave_type_id)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <i className="bi bi-calendar3 me-1 text-muted"></i>
                                                        {formatDate(entry.tdate)}
                                                    </td>
                                                    <td className="text-end">
                                                        {parseFloat(entry.earned) > 0 ? (
                                                            <span className="badge bg-success bg-opacity-10 text-success fw-normal fs-6 px-3 py-2">
                                                                <i className="bi bi-plus-circle me-1"></i>
                                                                {parseFloat(entry.earned).toFixed(2)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        {parseFloat(entry.consumed) > 0 ? (
                                                            <span className="badge bg-danger bg-opacity-10 text-danger fw-normal fs-6 px-3 py-2">
                                                                <i className="bi bi-dash-circle me-1"></i>
                                                                {parseFloat(entry.consumed).toFixed(2)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="small">
                                                            <i className="bi bi-clock me-1 text-muted"></i>
                                                            {formatDateTime(entry.create_on)}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="btn-group">
                                                            <button
                                                                className="btn btn-outline-info btn-sm"
                                                                onClick={() => handleView(entry)}
                                                                title="View Details"
                                                            >
                                                                <i className="bi bi-eye"></i>
                                                            </button>
                                                            {canEditEntry() && (
                                                                <button
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => handleEdit(entry)}
                                                                    title="Edit Entry"
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </button>
                                                            )}
                                                            {canDeleteEntry() && (
                                                                <button
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleDelete(entry)}
                                                                    title="Delete Entry"
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Table Footer Stats */}
                            {sortedEntries.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                                    <div className="small text-muted">
                                        Showing {sortedEntries.length} of {ledgerEntries.length} entries
                                    </div>
                                    <div className="small">
                                        <span className="me-3">
                                            <span className="text-muted">Total Earned:</span>
                                            <strong className="text-success ms-1">
                                                {filteredEntries.reduce((sum, e) => sum + parseFloat(e.earned || 0), 0).toFixed(2)} days
                                            </strong>
                                        </span>
                                        <span>
                                            <span className="text-muted">Total Consumed:</span>
                                            <strong className="text-danger ms-1">
                                                {filteredEntries.reduce((sum, e) => sum + parseFloat(e.consumed || 0), 0).toFixed(2)} days
                                            </strong>
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Entry Modal */}
            {showAddModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add Leave Ledger Entry
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowAddModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleAddSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="emp_id" className="form-label fw-medium">
                                                    Employee <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.emp_id ? 'is-invalid' : ''}`}
                                                    id="emp_id"
                                                    list="leave-ledger-employee-options"
                                                    value={employeeSearchText}
                                                    onChange={handleEmployeeSearchChange}
                                                    placeholder="Search employee by name or code"
                                                    autoComplete="off"
                                                    required
                                                />
                                                <datalist id="leave-ledger-employee-options">
                                                    {getCompanyEmployees().map(emp => (
                                                        <option key={emp.emp_id} value={getEmployeeOptionLabel(emp)} />
                                                    ))}
                                                </datalist>
                                                {validationErrors.emp_id && (
                                                    <div className="invalid-feedback">{validationErrors.emp_id}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="leave_type_id" className="form-label fw-medium">
                                                    Leave Type <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className={`form-select ${validationErrors.leave_type_id ? 'is-invalid' : ''}`}
                                                    id="leave_type_id"
                                                    name="leave_type_id"
                                                    value={formData.leave_type_id}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">Select Leave Type</option>
                                                    {leaveTypes.map(lt => (
                                                        <option key={lt.leave_type_id} value={lt.leave_type_id}>
                                                            {lt.leave_name} ({lt.leave_code})
                                                        </option>
                                                    ))}
                                                </select>
                                                {validationErrors.leave_type_id && (
                                                    <div className="invalid-feedback">{validationErrors.leave_type_id}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label htmlFor="tdate" className="form-label fw-medium">
                                                    Date <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className={`form-control ${validationErrors.tdate ? 'is-invalid' : ''}`}
                                                    id="tdate"
                                                    name="tdate"
                                                    value={formData.tdate}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {validationErrors.tdate && (
                                                    <div className="invalid-feedback">{validationErrors.tdate}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label htmlFor="earned" className="form-label fw-medium">
                                                    Earned Days
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="365"
                                                    className={`form-control ${validationErrors.earned ? 'is-invalid' : ''}`}
                                                    id="earned"
                                                    name="earned"
                                                    value={formData.earned}
                                                    onChange={handleChange}
                                                    placeholder="0.00"
                                                />
                                                {validationErrors.earned && (
                                                    <div className="invalid-feedback">{validationErrors.earned}</div>
                                                )}
                                                <div className="form-text">Leave days earned (e.g., 1.5, 2.75)</div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label htmlFor="consumed" className="form-label fw-medium">
                                                    Consumed Days
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="365"
                                                    className={`form-control ${validationErrors.consumed ? 'is-invalid' : ''}`}
                                                    id="consumed"
                                                    name="consumed"
                                                    value={formData.consumed}
                                                    onChange={handleChange}
                                                    placeholder="0.00"
                                                />
                                                {validationErrors.consumed && (
                                                    <div className="invalid-feedback">{validationErrors.consumed}</div>
                                                )}
                                                <div className="form-text">Leave days consumed (e.g., 0.5, 1.0)</div>
                                            </div>
                                        </div>
                                    </div>

                                    {validationErrors.balance_warning && (
                                        <div className="alert alert-warning">
                                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                            {validationErrors.balance_warning}
                                        </div>
                                    )}

                                    <div className="alert alert-info">
                                        <i className="bi bi-info-circle-fill me-2"></i>
                                        <strong>Note:</strong> You must enter either Earned days or Consumed days. Both can be entered together if needed.
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowAddModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add Entry
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Entry Modal */}
            {showEditModal && selectedEntry && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{
                                background: 'linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)',
                                borderBottom: 'none'
                            }}>
                                <h5 className="modal-title text-white">
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Edit Leave Ledger Entry #{selectedEntry.id}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>

                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="emp_id" className="form-label fw-medium">
                                                    Employee <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${validationErrors.emp_id ? 'is-invalid' : ''}`}
                                                    id="emp_id"
                                                    list="leave-ledger-employee-options"
                                                    value={employeeSearchText}
                                                    onChange={handleEmployeeSearchChange}
                                                    placeholder="Search employee by name or code"
                                                    autoComplete="off"
                                                    required
                                                />
                                                <datalist id="leave-ledger-employee-options">
                                                    {getCompanyEmployees().map(emp => (
                                                        <option key={emp.emp_id} value={getEmployeeOptionLabel(emp)} />
                                                    ))}
                                                </datalist>
                                                {validationErrors.emp_id && (
                                                    <div className="invalid-feedback">{validationErrors.emp_id}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="leave_type_id" className="form-label fw-medium">
                                                    Leave Type <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className={`form-select ${validationErrors.leave_type_id ? 'is-invalid' : ''}`}
                                                    id="leave_type_id"
                                                    name="leave_type_id"
                                                    value={formData.leave_type_id}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">Select Leave Type</option>
                                                    {leaveTypes.map(lt => (
                                                        <option key={lt.leave_type_id} value={lt.leave_type_id}>
                                                            {lt.leave_name} ({lt.leave_code})
                                                        </option>
                                                    ))}
                                                </select>
                                                {validationErrors.leave_type_id && (
                                                    <div className="invalid-feedback">{validationErrors.leave_type_id}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label htmlFor="tdate" className="form-label fw-medium">
                                                    Date <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className={`form-control ${validationErrors.tdate ? 'is-invalid' : ''}`}
                                                    id="tdate"
                                                    name="tdate"
                                                    value={formData.tdate}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {validationErrors.tdate && (
                                                    <div className="invalid-feedback">{validationErrors.tdate}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label htmlFor="earned" className="form-label fw-medium">
                                                    Earned Days
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="365"
                                                    className={`form-control ${validationErrors.earned ? 'is-invalid' : ''}`}
                                                    id="earned"
                                                    name="earned"
                                                    value={formData.earned}
                                                    onChange={handleChange}
                                                    placeholder="0.00"
                                                />
                                                {validationErrors.earned && (
                                                    <div className="invalid-feedback">{validationErrors.earned}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label htmlFor="consumed" className="form-label fw-medium">
                                                    Consumed Days
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="365"
                                                    className={`form-control ${validationErrors.consumed ? 'is-invalid' : ''}`}
                                                    id="consumed"
                                                    name="consumed"
                                                    value={formData.consumed}
                                                    onChange={handleChange}
                                                    placeholder="0.00"
                                                />
                                                {validationErrors.consumed && (
                                                    <div className="invalid-feedback">{validationErrors.consumed}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {validationErrors.balance_warning && (
                                        <div className="alert alert-warning">
                                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                            {validationErrors.balance_warning}
                                        </div>
                                    )}

                                    <div className="alert alert-info">
                                        <i className="bi bi-info-circle-fill me-2"></i>
                                        <strong>Note:</strong> Updating this entry will affect the employee's leave balance.
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-light border-secondary-subtle"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-white"
                                    >
                                        <i className="bi bi-check-circle me-2"></i>
                                        Update Entry
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* View Entry Modal */}
            {showViewModal && selectedEntry && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-eye me-2"></i>
                                    Leave Ledger Entry Details
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowViewModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-sm">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="40%">Entry ID:</th>
                                                    <td>
                                                        <span className="badge bg-secondary">#{selectedEntry.id}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Employee:</th>
                                                    <td>
                                                        <div className="fw-medium">
                                                            {getEmployeeName(selectedEntry.emp_id)}
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Leave Type:</th>
                                                    <td>
                                                        <span className="badge bg-info">
                                                            {getLeaveTypeName(selectedEntry.leave_type_id)}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Date:</th>
                                                    <td>
                                                        <i className="bi bi-calendar3 me-1 text-muted"></i>
                                                        {formatDate(selectedEntry.tdate)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-sm">
                                            <tbody>
                                                <tr>
                                                    <th className="text-muted" width="40%">Company:</th>
                                                    <td>{getCompanyName(selectedEntry.company_id)}</td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Earned:</th>
                                                    <td>
                                                        <span className="badge bg-success px-3 py-2">
                                                            <i className="bi bi-plus-circle me-1"></i>
                                                            {parseFloat(selectedEntry.earned).toFixed(2)} days
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Consumed:</th>
                                                    <td>
                                                        <span className="badge bg-danger px-3 py-2">
                                                            <i className="bi bi-dash-circle me-1"></i>
                                                            {parseFloat(selectedEntry.consumed).toFixed(2)} days
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="text-muted">Created On:</th>
                                                    <td>
                                                        <i className="bi bi-clock me-1 text-muted"></i>
                                                        {formatDateTime(selectedEntry.create_on)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="mt-3 p-3 bg-light rounded">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-medium">Net Effect on Balance:</span>
                                        <span className={`fs-5 fw-bold ${parseFloat(selectedEntry.earned) > parseFloat(selectedEntry.consumed) ? 'text-success' : 'text-danger'}`}>
                                            {parseFloat(selectedEntry.earned) > parseFloat(selectedEntry.consumed) ? '+' : ''}
                                            {(parseFloat(selectedEntry.earned || 0) - parseFloat(selectedEntry.consumed || 0)).toFixed(2)} days
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                                {canEditEntry() && (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setShowViewModal(false);
                                            handleEdit(selectedEntry);
                                        }}
                                    >
                                        <i className="bi bi-pencil me-2"></i>
                                        Edit Entry
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedEntry && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-danger text-white">
                                <h5 className="modal-title">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Confirm Delete
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="alert alert-warning" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <strong>Warning:</strong> This action cannot be undone.
                                </div>

                                <p>Are you sure you want to delete this leave ledger entry?</p>

                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <h6 className="fw-bold mb-3">Entry Details:</h6>
                                        <div className="row">
                                            <div className="col-6">
                                                <small className="text-muted d-block">Employee</small>
                                                <strong>{getEmployeeName(selectedEntry.emp_id)}</strong>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-muted d-block">Leave Type</small>
                                                <strong>{getLeaveTypeName(selectedEntry.leave_type_id)}</strong>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-6">
                                                <small className="text-muted d-block">Date</small>
                                                <strong>{formatDate(selectedEntry.tdate)}</strong>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-muted d-block">Entry ID</small>
                                                <span className="badge bg-secondary">#{selectedEntry.id}</span>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            <div className="col-6">
                                                <small className="text-muted d-block">Earned</small>
                                                <span className="badge bg-success">
                                                    {parseFloat(selectedEntry.earned).toFixed(2)} days
                                                </span>
                                            </div>
                                            <div className="col-6">
                                                <small className="text-muted d-block">Consumed</small>
                                                <span className="badge bg-danger">
                                                    {parseFloat(selectedEntry.consumed).toFixed(2)} days
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-danger mt-3" role="alert">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <strong>Note:</strong> Deleting this entry will affect the employee's leave balance.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light border-secondary-subtle"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                >
                                    <i className="bi bi-trash me-2"></i>
                                    Delete Entry
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ImportLeaves
                show={showImportModal}
                onHide={() => setShowImportModal(false)}
                onImport={handleImportLeaves}
                canImportLeaves={canImportLeaves()}
                error={error}
                setError={setError}
                API_BASE={API_BASE}
            />
        </div>
    );
};

export default LeaveLedger;
